import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { HelpBubble } from '@/components/HelpBubble';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Download, RefreshCw, AlertTriangle, ShieldCheck } from 'lucide-react';
import { TrustScoreBadge } from '@/components/TrustScoreBadge';
import { useCurrency } from '@/lib/useCurrency';
import api from '@/lib/api';

export default function InvoiceDedup() {
  const { symbol: cs } = useCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState('at_risk');

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/analytics/invoice-dedup');
      setData(res.data);
    } catch { setData(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const fmt = (v) => `${cs}${(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

  const sorted = (() => {
    if (!data?.groups) return [];
    const arr = [...data.groups];
    arr.sort((a, b) => (b[sortKey] || 0) - (a[sortKey] || 0));
    return arr;
  })();

  const exportCsv = () => {
    if (!sorted.length) return;
    const header = 'Supplier,Invoice #,Occurrences,Distinct Amounts,Distinct Dates,Total Spend,At Risk';
    const rows = sorted.map(g => [
      `"${g.supplier_name}"`, `"${g.invoice_number}"`, g.occurrences,
      g.distinct_amounts, g.distinct_dates, g.total_spend, g.at_risk,
    ].join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'invoice-dedup.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6 space-y-4" data-testid="invoice-dedup-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Potential Duplicate Payments</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Groups transactions by <code className="text-xs bg-muted px-1 py-0.5 rounded">(invoice_number, supplier)</code> and flags
            collisions. &ldquo;At risk&rdquo; = total spend minus the minimum amount (assumes one legitimate payment).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TrustScoreBadge pageName="invoice-dedup" />
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!sorted.length} data-testid="export-csv-button">
            <Download className="w-4 h-4 mr-2" />CSV
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="refresh-button">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
        </div>
      </div>

      {!data?.has_data ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">No data available</CardContent></Card>
      ) : !data.coverage_ok ? (
        <Alert className="border-amber-300 bg-amber-50 dark:bg-amber-950/30" data-testid="coverage-warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Invoice number coverage too low for reliable dedup</AlertTitle>
          <AlertDescription>
            Only {data.coverage_pct}% of your {data.total_records?.toLocaleString()} records have an
            invoice number populated (minimum {data.threshold_pct}%). Upload a richer DataRefinery
            export that carries <code className="text-xs bg-background px-1 py-0.5 rounded">invoice_number</code> / <code className="text-xs bg-background px-1 py-0.5 rounded">invoice_id</code> on every row.
          </AlertDescription>
        </Alert>
      ) : sorted.length === 0 ? (
        <Card className="border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20" data-testid="no-duplicates">
          <CardHeader className="flex flex-row items-start gap-3">
            <ShieldCheck className="w-6 h-6 text-emerald-600 mt-0.5" />
            <div>
              <CardTitle className="text-emerald-900 dark:text-emerald-100">No duplicate payments detected</CardTitle>
              <CardDescription>
                Scanned {data.records_with_invoice?.toLocaleString()} rows with invoice numbers. All (invoice #, supplier) pairs are unique.
              </CardDescription>
            </div>
          </CardHeader>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Card data-testid="dedup-kpi-groups">
              <CardContent className="py-4">
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  SUSPECT GROUPS
                  <HelpBubble text="Groups of rows sharing the same invoice number and supplier — each group is a probable duplicate payment worth reviewing." />
                </div>
                <div className="text-2xl font-bold font-mono">{data.duplicate_groups}</div>
              </CardContent>
            </Card>
            <Card data-testid="dedup-kpi-at-risk">
              <CardContent className="py-4">
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  TOTAL AT RISK
                  <HelpBubble text="Sum of the duplicate rows excluding one legitimate copy per group (sum minus minimum) — the cash potentially paid twice." />
                </div>
                <div className="text-2xl font-bold font-mono text-rose-600">{fmt(data.total_at_risk)}</div>
              </CardContent>
            </Card>
            <Card data-testid="dedup-kpi-coverage">
              <CardContent className="py-4">
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  INVOICE COVERAGE
                  <HelpBubble text="Share of records carrying an invoice number. Detection only runs on invoiced rows, so low coverage means duplicates may be hiding in the rest of the data." />
                </div>
                <div className="text-2xl font-bold font-mono">{data.coverage_pct}%</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {data.records_with_invoice?.toLocaleString()} of {data.total_records?.toLocaleString()} records
                </div>
              </CardContent>
            </Card>
          </div>

          <Card data-testid="dedup-table">
            <CardHeader>
              <CardTitle>Suspect groups</CardTitle>
              <CardDescription>Click a column header to sort. Top 200 by at-risk amount shown.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2">Supplier</th>
                      <th className="text-left py-2 px-2">Invoice #</th>
                      <th className="text-center py-2 px-2 cursor-pointer hover:text-primary" onClick={() => setSortKey('occurrences')}>Occ.</th>
                      <th className="text-center py-2 px-2">Distinct Amounts</th>
                      <th className="text-center py-2 px-2">Distinct Dates</th>
                      <th className="text-right py-2 px-2 cursor-pointer hover:text-primary" onClick={() => setSortKey('total_spend')}>Total Spend</th>
                      <th className="text-right py-2 px-2 cursor-pointer hover:text-primary" onClick={() => setSortKey('at_risk')}>At Risk</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((g, i) => (
                      <tr key={i} className="border-b hover:bg-muted/50" data-testid={`dedup-row-${i}`}>
                        <td className="py-1.5 px-2 font-medium">{g.supplier_name}</td>
                        <td className="py-1.5 px-2 font-mono text-xs">{g.invoice_number}</td>
                        <td className="py-1.5 px-2 text-center">
                          <Badge variant={g.occurrences > 2 ? 'destructive' : 'secondary'}>{g.occurrences}</Badge>
                        </td>
                        <td className="py-1.5 px-2 text-center">
                          {g.distinct_amounts > 1 ? (
                            <Badge variant="destructive">{g.distinct_amounts}</Badge>
                          ) : g.distinct_amounts}
                        </td>
                        <td className="py-1.5 px-2 text-center">
                          {g.distinct_dates > 1 ? (
                            <Badge variant="destructive">{g.distinct_dates}</Badge>
                          ) : g.distinct_dates}
                        </td>
                        <td className="py-1.5 px-2 text-right font-mono">{fmt(g.total_spend)}</td>
                        <td className="py-1.5 px-2 text-right font-mono text-rose-600">{fmt(g.at_risk)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
